import { useMemo } from "react";
import { notification, NotificationArgsProps } from "antd";
import { ConfigProviderExtended } from "@/components/ConfigProviderExtended";
import { poppins } from "@/components/common/fonts";
import styled from "styled-components";
import {
  CheckCircleFilled,
  InfoCircleFilled,
  ExclamationCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";

const NotificationMessage = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin: 0 15px 0 0;
  padding: 0;
`;

const NotificationText = styled.p`
  flex: 0 0 auto;
  max-width: calc(100% - 20px - 22px);
  margin: 0;
  padding: 0;
  text-align: left;
  font-weight: 500;
  /* overflow: hidden; */
  /* white-space: nowrap; */
  /* text-overflow: ellipsis; */
`;

const NotificationSuccessIcon = styled(CheckCircleFilled)`
  font-size: 22px;
`;

const NotificationInfoIcon = styled(InfoCircleFilled)`
  font-size: 22px;
`;

const NotificationWarningIcon = styled(ExclamationCircleFilled)`
  font-size: 22px;
`;

const NotificationErrorIcon = styled(CloseCircleFilled)`
  font-size: 22px;
`;

/**
 * string 또는 antd의 기존 notification의 오브젝트 형태의 파라미터를 받을 수 있도록 함
 */
type NotifyParams = string | NotificationArgsProps;

const objectify = (msg: NotifyParams) => {
  if (typeof msg === "string") {
    return { message: msg };
  }
  return msg;
};

export function useNotification(props?: {
  stack?: boolean;
  container?: ReturnType<Required<Required<Parameters<typeof notification.useNotification>>[0]>["getContainer"]> | null;
}) {
  const options = useMemo(() => {
    const list: Partial<Parameters<typeof notification.useNotification>>[0] = {
      placement: "top",
      duration: 4,
    };

    const stack = props?.stack || false;

    if (stack) {
      list.stack = { threshold: 1 };
    } else {
      list.stack = false;
      list.maxCount = 1;
    }

    const container = props?.container;

    if (container) {
      list.getContainer = () => container;
    }

    return list;
  }, [props?.container, props?.stack]);

  const [notificationApi, notificationContextHolder] =
    notification.useNotification({
      placement: "top",
      duration: 4,
      ...options,
    });

  const notify = useMemo(
    () => ({
      success: (msg: NotifyParams) => {
        const objectified = objectify(msg);

        notificationApi.success({
          icon: <></>,
          ...objectified,
          message: (
            <NotificationMessage>
              <NotificationSuccessIcon />
              <NotificationText>{objectified.message}</NotificationText>
            </NotificationMessage>
          ),
          style: {
            backgroundColor: "#2bab62",
            borderRadius: "8px",
            ...objectified.style,
          },
        });
      },
      info: (msg: NotifyParams) => {
        const objectified = objectify(msg);

        notificationApi.info({
          icon: <></>,
          ...objectified,
          message: (
            <NotificationMessage>
              <NotificationInfoIcon />
              <NotificationText>{objectified.message}</NotificationText>
            </NotificationMessage>
          ),
          style: {
            backgroundColor: "#1677ff",
            borderRadius: "8px",
            ...objectified.style,
          },
        });
      },
      warning: (msg: NotifyParams) => {
        const objectified = objectify(msg);

        notificationApi.warning({
          icon: <></>,
          ...objectified,
          message: (
            <NotificationMessage>
              <NotificationWarningIcon />
              <NotificationText>{objectified.message}</NotificationText>
            </NotificationMessage>
          ),
          style: {
            backgroundColor: "#e67f3a",
            borderRadius: "8px",
            ...objectified.style,
          },
        });
      },
      error: (msg: NotifyParams) => {
        const objectified = objectify(msg);

        notificationApi.error({
          icon: <></>,
          ...objectified,
          message: (
            <NotificationMessage>
              <NotificationErrorIcon />
              <NotificationText>{objectified.message}</NotificationText>
            </NotificationMessage>
          ),
          style: {
            backgroundColor: "#ff4d4f",
            borderRadius: "8px",
            ...objectified.style,
          },
        });
      },
    }),
    [notificationApi],
  );

  const contextHolder = useMemo(() => {
    return (
      <ConfigProviderExtended
        theme={{
          components: {
            Notification: {
              width: 760,
              colorTextHeading: "#fff",
              fontFamily: poppins.string,
              fontSize: 16,
              colorIcon: "#fff",
              colorIconHover: "#fff",
            },
          },
        }}
        space="small"
      >
        {notificationContextHolder}
      </ConfigProviderExtended>
    );
  }, [notificationContextHolder]);

  return { notify, contextHolder };
}

export type Notify = ReturnType<typeof useNotification>["notify"];